import React from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { AutoComplete } from 'primereact/autocomplete';
import acordLogo from 'images/acordlogo.png';
import CreateNewCarriers from '../CreateNewCarriers';
import AddNewSignature from '../AddNewSignature';
import { useSignatures } from '../../../../hooks/useSignatures';
import ProducerSection, { fetchPolicyAgentContact } from '../ProducerSection';
import { useCertificateHolders } from '../../../certificate-holders/hooks/useCertificateHolders';
import InsurerSearchDropdown from '../InsurerSearchDropdown';
import { formatUsPhone } from 'utilities/formatPhone';

const INSR_OPTIONS = ['A','B','C','D','E','F'].map(l => ({ label: l, value: l }));

interface GenericRow {
  code: string;
  desc: string;
  limit1: string;
  appliesTo1: string;
  limit2: string;
  appliesTo2: string;
}
const emptyRow = (): GenericRow => ({ code: '', desc: '', limit1: '', appliesTo1: '', limit2: '', appliesTo2: '' });

interface Acord21FormProps { selectedPolicies?: string[]; insuredId?: string; }

export default function Acord21Form({ selectedPolicies = [], insuredId = '' }: Acord21FormProps) {
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);
  const [showAddSignature, setShowAddSignature] = React.useState(false);

  const [producerAddress, setProducerAddress] = React.useState('');
  const [producerCity, setProducerCity] = React.useState('');
  const [producerState, setProducerState] = React.useState('');
  const [producerZip, setProducerZip] = React.useState('');
  const [producerStreet, setProducerStreet] = React.useState('');

  const [contactName, setContactName] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [contactFax, setContactFax] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [producerCustomerId, setProducerCustomerId] = React.useState('');

  const [insuredName, setInsuredName] = React.useState('');
  const [insuredAddress, setInsuredAddress] = React.useState('');
  const [insuredCity, setInsuredCity] = React.useState('');
  const [insuredState, setInsuredState] = React.useState('');
  const [insuredZip, setInsuredZip] = React.useState('');

  const [insurerA, setInsurerA] = React.useState('');
  const [insurerB, setInsurerB] = React.useState('');
  const [insurerC, setInsurerC] = React.useState('');
  const [insurerD, setInsurerD] = React.useState('');
  const [insurerE, setInsurerE] = React.useState('');
  const [insurerF, setInsurerF] = React.useState('');

  const [certDate, setCertDate] = React.useState<Date | null>(new Date());
  const [certNumber, setCertNumber] = React.useState('');
  const [revisionNumber, setRevisionNumber] = React.useState('');

  const [ptIndustrialAid, setPtIndustrialAid] = React.useState(false);
  const [ptPleasureBus, setPtPleasureBus] = React.useState(false);
  const [ptCommercial, setPtCommercial] = React.useState(false);
  const [ptNonOwned, setPtNonOwned] = React.useState(false);
  const [ptOtherCheck, setPtOtherCheck] = React.useState(false);
  const [ptOtherText, setPtOtherText] = React.useState('');

  const [lbAirplane, setLbAirplane] = React.useState(false);
  const [lbHelicopter, setLbHelicopter] = React.useState(false);
  const [lbMixedFleet, setLbMixedFleet] = React.useState(false);
  const [lbExcess, setLbExcess] = React.useState(false);
  const [lbQuotaShare, setLbQuotaShare] = React.useState(false);
  const [lbLiabilityOnly, setLbLiabilityOnly] = React.useState(false);
  const [lbHullLiability, setLbHullLiability] = React.useState(false);
  const [lbHullOnly, setLbHullOnly] = React.useState(false);
  const [lbOtherCheck, setLbOtherCheck] = React.useState(false);
  const [lbOtherText, setLbOtherText] = React.useState('');

  const [aiAcordSchedule, setAiAcordSchedule] = React.useState(false);
  const [aiYear, setAiYear] = React.useState('');
  const [aiMake, setAiMake] = React.useState('');
  const [aiModel, setAiModel] = React.useState('');
  const [aiSerialNumber, setAiSerialNumber] = React.useState('');
  const [aiRegistrationNumber, setAiRegistrationNumber] = React.useState('');
  const [aiTerritory, setAiTerritory] = React.useState('');

  const [acInsurer, setAcInsurer] = React.useState('');
  const [acPolicyNum, setAcPolicyNum] = React.useState('');
  const [acEffDate, setAcEffDate] = React.useState<Date | null>(null);
  const [acExpDate, setAcExpDate] = React.useState<Date | null>(null);
  const [acAddlInsured, setAcAddlInsured] = React.useState('');
  const [acSubrWaived, setAcSubrWaived] = React.useState('');

  const [medIncludingCrew, setMedIncludingCrew] = React.useState(false);
  const [medExcludingCrew, setMedExcludingCrew] = React.useState(false);

  const [genericRows, setGenericRows] = React.useState<GenericRow[]>(
    Array.from({ length: 3 }, emptyRow)
  );

  const [descriptionOfOps, setDescriptionOfOps] = React.useState('');
  const [holderName, setHolderName] = React.useState('');
  const [holderAddress, setHolderAddress] = React.useState('');
  const [holderCity, setHolderCity] = React.useState('');
  const [holderState, setHolderState] = React.useState('');
  const [holderZip, setHolderZip] = React.useState('');
  const [holderSearchQuery, setHolderSearchQuery] = React.useState('');
  const { holders: holderSuggestions } = useCertificateHolders(holderSearchQuery, 10);
  const [authRepSignature, setAuthRepSignature] = React.useState('');
  const { signatureOptions, refetchSignatures } = useSignatures();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = React.useState('');
  React.useEffect(() => {
    if (!authRepSignature) { setSignaturePreviewUrl(''); return; }
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-preview/${authRepSignature}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(url => setSignaturePreviewUrl(url.replace(/^"|"$/g, '')))
      .catch(() => setSignaturePreviewUrl(''));
  }, [authRepSignature]);

  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;
    let cancelled = false;
    const fetchCoverages = async () => {
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';
        const res = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/get-coverages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'NCToken': ncToken,
              Authorization: `Bearer ${igToken}`,
            },
            body: JSON.stringify({
              policiesIds: selectedPolicies,
              truckingCompanyId: insuredId || '',
              fileParentId: '0A84CBFD-C660-4E5B-B089-23065CA3D981',
              naics: [{ Code: '', Name: '' }],
              Page: 'certificateFiller',
            }),
          }
        );
        if (cancelled) return;
        await res.json();
      } catch {
        // silently fail — user can still fill manually
      }
    };
    fetchCoverages();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicies, insuredId]);

  // Prefill contact section from the first selected policy's agent
  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;
    fetchPolicyAgentContact(selectedPolicies[0]).then(({ name, phone, fax, email }) => {
      if (name) setContactName(name);
      if (phone) setContactPhone(phone);
      if (fax) setContactFax(fax);
      if (email) setContactEmail(email);
    });
  }, [selectedPolicies]);

  const chk = (id: string, label: string, checked: boolean, onChange: (v: boolean) => void) => (
    <div className="masterCert__checkboxItem">
      <Checkbox inputId={id} checked={checked} onChange={(e) => onChange(e.checked ?? false)} />
      <label htmlFor={id} className="masterCert__checkboxItemLabel">{label}</label>
    </div>
  );

  const limitInput = (style?: React.CSSProperties) => (
    <div className="acord20__limitCell">
      <span className="acord20__limitDollar">$</span>
      <InputText className="masterCert__input" style={{ width: 70, ...style }} />
    </div>
  );

  const covRow = (
    name: string | React.ReactNode,
    options: React.ReactNode,
    appliesTo1: string,
    appliesTo2: string,
    isSub?: boolean,
  ) => (
    <div className={`acord20__covRow ${isSub ? 'acord20__covRow--sub' : ''}`}>
      <div className="acord20__covName">{name}</div>
      <div className="acord20__covOptions">{options}</div>
      {limitInput()}
      <div className="acord20__appliesTo">{appliesTo1}</div>
      {appliesTo2 ? limitInput() : <div />}
      <div className="acord20__appliesTo">{appliesTo2}</div>
    </div>
  );

  const updateGenericRow = (i: number, field: keyof GenericRow, value: string) => {
    setGenericRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  };

  return (
    <>
      <div className="masterCert__section">
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">CERTIFICATE OF AIRCRAFT INSURANCE</div>
          </div>
          <div className="masterCert__dateWrapper">
            <div className="masterCert__dateLabel">DATE (MM/DD/YYYY)</div>
            <div className="masterCert__dateCell">
              <Calendar value={certDate} onChange={(e) => setCertDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
          </div>
        </div>

        <div className="masterCert__disclaimer">
          This certificate is issued as a matter of information only and confers no rights upon the certificate holder. This certificate does not affirmatively or negatively amend, extend or alter the coverage afforded by the policies below. This certificate of insurance does not constitute a contract between the issuing insurer(s), authorized representative or producer, and the certificate holder.
        </div>
        <div className="masterCert__disclaimerImportant">
          <span className="masterCert__importantLabel">IMPORTANT:</span> If the certificate holder is an Additional Insured, the policy(ies) must be endorsed. If subrogation is waived, subject to the terms and conditions of the policy, certain policies may require an endorsement. A statement on this certificate does not confer rights to the certificate holder in lieu of such endorsement(s).
        </div>

        <div className="masterCert__twoColumnLayout">
          <div>
            <ProducerSection
              address={producerAddress}
              onAddressChange={setProducerAddress}
              city={producerCity}
              onCityChange={setProducerCity}
              street={producerStreet}
              onStreetChange={setProducerStreet}
              state={producerState}
              onStateChange={setProducerState}
              zip={producerZip}
              onZipChange={setProducerZip}
              onAgentContact={(phone, fax, email) => { setContactPhone(phone); setContactFax(fax); setContactEmail(email); }}
              readOnly
            />
            <div>
              <div className="masterCert__sectionHeader">INSURED</div>
              <Dropdown value={insuredName} onChange={(e) => setInsuredName(e.value)} options={[]} placeholder="Type and/or choose location" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" editable disabled emptyMessage="No available options" />
              <InputText maxLength={100} value={insuredAddress} onChange={(e) => setInsuredAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" readOnly />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={insuredCity} onChange={(e) => setInsuredCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" readOnly />
                <InputText maxLength={50} value={insuredState} onChange={(e) => setInsuredState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" readOnly />
                <InputText maxLength={20} value={insuredZip} onChange={(e) => setInsuredZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" readOnly />
              </div>
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">CONTACT NAME:</div>
                  <InputText maxLength={100} value={contactName} onChange={(e) => setContactName(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
                <div></div>
              </div>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">PHONE (A/C, No, Ext):</div>
                  <InputText value={formatUsPhone(contactPhone)} onChange={(e) => setContactPhone(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
                <div>
                  <div className="masterCert__contactLabel">FAX (A/C, No):</div>
                  <InputText value={formatUsPhone(contactFax)} onChange={(e) => setContactFax(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
                </div>
              </div>
              <div>
                <div className="masterCert__contactLabel">E-MAIL ADDRESS:</div>
                <InputText maxLength={100} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="masterCert__input masterCert__inputFull" readOnly />
              </div>
              <div style={{ marginTop: 6 }}>
                <div className="masterCert__contactLabel">PRODUCER CUSTOMER ID #:</div>
                <InputText maxLength={30} value={producerCustomerId} onChange={(e) => setProducerCustomerId(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>INSURERS AFFORDING COVERAGE (and NAIC #)</div>
                <div className="acord20__insurerPctLabel" style={{ marginLeft: 'auto' }}>%</div>
              </div>
              <div className="acord20__insurersGrid">
                <div className="masterCert__insurerLabel">INSURER A</div>
                <InsurerSearchDropdown value={insurerA} onChange={setInsurerA} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER B</div>
                <InsurerSearchDropdown value={insurerB} onChange={setInsurerB} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER C</div>
                <InsurerSearchDropdown value={insurerC} onChange={setInsurerC} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER D</div>
                <InsurerSearchDropdown value={insurerD} onChange={setInsurerD} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER E</div>
                <InsurerSearchDropdown value={insurerE} onChange={setInsurerE} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER F</div>
                <InsurerSearchDropdown value={insurerF} onChange={setInsurerF} className="masterCert__input masterCert__inputFull" />
                <InputText className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
          </div>
        </div>

        <div className="masterCert__disclaimer" style={{ marginTop: 12 }}>
          This is to certify that the policies of insurance listed below have been issued to the insured named above for the policy period indicated. Notwithstanding any requirement, term or condition of any contract or other document with respect to which this certificate may be issued or may pertain, the insurance afforded by the policies described herein is subject to all the terms, exclusions and conditions of such policies. Limits shown may have been reduced by paid claims.
        </div>

        <div className="acord20__sectionTitle">POLICY INFORMATION</div>

        <div className="masterCert__certNumberGrid">
          <div>
            <div className="masterCert__contactLabel">CERTIFICATE NUMBER:</div>
            <InputText maxLength={30} value={certNumber} onChange={(e) => setCertNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
          </div>
          <div>
            <div className="masterCert__contactLabel">REVISION NUMBER:</div>
            <InputText maxLength={30} value={revisionNumber} onChange={(e) => setRevisionNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
          </div>
        </div>

        <div className="acord21__policyTypeBox">
          <div className="acord21__policyTypeTitle">POLICY TYPE</div>
          <div className="acord21__policyTypeGrid">
            {chk('a21PtIndustrialAid', 'INDUSTRIAL AID', ptIndustrialAid, setPtIndustrialAid)}
            {chk('a21PtPleasureBus', 'PLEASURE & BUS', ptPleasureBus, setPtPleasureBus)}
            {chk('a21PtCommercial', 'COMMERCIAL', ptCommercial, setPtCommercial)}
            {chk('a21PtNonOwned', 'NON-OWNED', ptNonOwned, setPtNonOwned)}
            <div className="masterCert__checkboxItem">
              <Checkbox inputId="a21PtOther" checked={ptOtherCheck} onChange={(e) => setPtOtherCheck(e.checked ?? false)} />
              <InputText value={ptOtherText} onChange={(e) => setPtOtherText(e.target.value)} className="masterCert__input" style={{ width: 140 }} />
            </div>
          </div>
        </div>

        <div className="acord21__subcodeBox">
          <div className="acord21__subcodeTitle">LINE OF BUSINESS SUBCODE</div>
          <div className="acord21__subcodeGrid">
            {chk('a21LbAirplane', 'AIRPLANE', lbAirplane, setLbAirplane)}
            {chk('a21LbHelicopter', 'HELICOPTER', lbHelicopter, setLbHelicopter)}
            {chk('a21LbMixedFleet', 'MIXED FLEET', lbMixedFleet, setLbMixedFleet)}
            {chk('a21LbExcess', 'EXCESS', lbExcess, setLbExcess)}
            {chk('a21LbQuotaShare', 'QUOTA SHARE', lbQuotaShare, setLbQuotaShare)}
          </div>
          <div className="acord21__subcodeGrid" style={{ marginTop: 8 }}>
            {chk('a21LbLiabilityOnly', 'LIABILITY ONLY', lbLiabilityOnly, setLbLiabilityOnly)}
            {chk('a21LbHullLiability', 'HULL & LIABILITY', lbHullLiability, setLbHullLiability)}
            {chk('a21LbHullOnly', 'HULL ONLY', lbHullOnly, setLbHullOnly)}
            <div className="masterCert__checkboxItem">
              <Checkbox inputId="a21LbOther" checked={lbOtherCheck} onChange={(e) => setLbOtherCheck(e.checked ?? false)} />
              <InputText value={lbOtherText} onChange={(e) => setLbOtherText(e.target.value)} className="masterCert__input" style={{ width: 140 }} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, marginTop: 16 }}>
          <div className="acord20__sectionTitle" style={{ margin: 0 }}>AIRCRAFT INFORMATION</div>
          {chk('a21AiAcordSchedule', 'ACORD 333, Aircraft Schedule Attached', aiAcordSchedule, setAiAcordSchedule)}
        </div>

        <div className="acord21__aircraftInfoHeader">
          <div>YEAR</div>
          <div>MAKE</div>
          <div>MODEL</div>
          <div>SERIAL NUMBER</div>
          <div>REGISTRATION NUMBER</div>
        </div>
        <div className="acord21__aircraftInfoRow">
          <InputText maxLength={50} value={aiYear} onChange={(e) => setAiYear(e.target.value)} className="masterCert__input masterCert__inputFull" />
          <InputText maxLength={50} value={aiMake} onChange={(e) => setAiMake(e.target.value)} className="masterCert__input masterCert__inputFull" />
          <InputText maxLength={50} value={aiModel} onChange={(e) => setAiModel(e.target.value)} className="masterCert__input masterCert__inputFull" />
          <InputText maxLength={50} value={aiSerialNumber} onChange={(e) => setAiSerialNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
          <InputText maxLength={50} value={aiRegistrationNumber} onChange={(e) => setAiRegistrationNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <div className="masterCert__contactLabel" style={{ whiteSpace: 'nowrap' }}>TERRITORY:</div>
          <InputText maxLength={50} value={aiTerritory} onChange={(e) => setAiTerritory(e.target.value)} className="masterCert__input masterCert__inputFull" />
        </div>

        {/* ═══ AIRCRAFT COVERAGES ═══ */}
        <div className="acord20__sectionTitle">AIRCRAFT COVERAGES</div>

        {/* Policy info row */}
        <div className="acord20__policyInfoHeader">
          <div style={{ textAlign: 'left' }}>INSURER LETTER</div>
          <div>POLICY NUMBER</div>
          <div>EFFECTIVE DATE</div>
          <div>EXPIRATION DATE</div>
          <div>ADDITIONAL INSURED? (Y/N)</div>
          <div>SUBROGATION WAIVED? (Y/N)</div>
        </div>
        <div className="acord20__policyInfoRow">
          <Dropdown value={acInsurer} onChange={(e) => setAcInsurer(e.value)} options={INSR_OPTIONS} placeholder="" className="masterCert__input masterCert__inputFull" emptyMessage="No available options" />
          <InputText value={acPolicyNum} onChange={(e) => setAcPolicyNum(e.target.value)} className="masterCert__input masterCert__inputFull" />
          <div className="masterCert__dateCell">
            <Calendar value={acEffDate} onChange={(e) => setAcEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
          </div>
          <div className="masterCert__dateCell">
            <Calendar value={acExpDate} onChange={(e) => setAcExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
          </div>
          <InputText value={acAddlInsured} onChange={(e) => setAcAddlInsured(e.target.value)} className="masterCert__input masterCert__inputFull" />
          <InputText value={acSubrWaived} onChange={(e) => setAcSubrWaived(e.target.value)} className="masterCert__input masterCert__inputFull" />
        </div>

        <div className="acord20__covHeader">
          <div>COVERAGE</div>
          <div>OPTIONS</div>
          <div>LIMIT</div>
          <div>APPLIES TO</div>
          <div>LIMIT</div>
          <div>APPLIES TO</div>
        </div>

        {covRow('AIRCRAFT HULL',
          <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          '', ''
        )}
        {covRow('',
          <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          '', '', true
        )}

        {covRow('AIRCRAFT LIABILITY',
          <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'EA OCC', 'EA PER'
        )}
        {covRow('',
          <><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /><Checkbox checked={false} /><InputText className="masterCert__input" style={{ width: 80 }} /></>,
          'EA PASS', 'AGGR', true
        )}

        <div className="acord20__covRow">
          <div className="acord20__covName">MEDICAL PAYMENTS</div>
          <div className="acord20__covOptions">
            {chk('a21MedInclCrew', 'INCLUDING CREW', medIncludingCrew, setMedIncludingCrew)}
            {chk('a21MedExclCrew', 'EXCLUDING CREW', medExcludingCrew, setMedExcludingCrew)}
          </div>
          {limitInput()}
          <div className="acord20__appliesTo">EA PER</div>
          <div /><div />
        </div>

        <div className="acord20__genericHeader">
          <div>CODE</div>
          <div>DESCRIPTION</div>
          <div>OPTIONS</div>
          <div>LIMIT</div>
          <div>APPLIES TO</div>
          <div>LIMIT</div>
          <div>APPLIES TO</div>
        </div>
        {genericRows.map((row, i) => (
          <React.Fragment key={i}>
            <div className="acord20__genericRow">
              <InputText value={row.code} onChange={(e) => updateGenericRow(i, 'code', e.target.value)} className="masterCert__input masterCert__inputFull" />
              <InputText value={row.desc} onChange={(e) => updateGenericRow(i, 'desc', e.target.value)} className="masterCert__input masterCert__inputFull" />
              <div className="acord20__covOptions">
                <Checkbox checked={false} />
                <InputText className="masterCert__input" style={{ width: 80 }} />
                <Checkbox checked={false} />
                <InputText className="masterCert__input" style={{ width: 80 }} />
              </div>
              <div className="acord20__limitCell">
                <span className="acord20__limitDollar">$</span>
                <InputText value={row.limit1} onChange={(e) => updateGenericRow(i, 'limit1', e.target.value)} className="masterCert__input" style={{ width: 60 }} />
              </div>
              <InputText value={row.appliesTo1} onChange={(e) => updateGenericRow(i, 'appliesTo1', e.target.value)} className="masterCert__input masterCert__inputFull" />
              <div className="acord20__limitCell">
                <span className="acord20__limitDollar">$</span>
                <InputText value={row.limit2} onChange={(e) => updateGenericRow(i, 'limit2', e.target.value)} className="masterCert__input" style={{ width: 60 }} />
              </div>
              <InputText value={row.appliesTo2} onChange={(e) => updateGenericRow(i, 'appliesTo2', e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>

            <div className="acord20__genericRow acord20__genericRow--sub">
              <div /><div />
              <div className="acord20__covOptions">
                <Checkbox checked={false} />
                <InputText className="masterCert__input" style={{ width: 80 }} />
                <Checkbox checked={false} />
                <InputText className="masterCert__input" style={{ width: 80 }} />
              </div>
              {limitInput({ width: 60 })}
              <InputText className="masterCert__input masterCert__inputFull" />
              {limitInput({ width: 60 })}
              <InputText className="masterCert__input masterCert__inputFull" />
            </div>
          </React.Fragment>
        ))}

        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">DESCRIPTION OF OPERATIONS / REMARKS (Attach ACORD 101, Additional Remarks Schedule, if more space is required)</div>
          <InputTextarea maxLength={2000} value={descriptionOfOps} onChange={(e) => setDescriptionOfOps(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>

        <div className="masterCert__bottomRow">
          <div className="masterCert__certHolderBox">
            <div className="masterCert__sectionHeader">CERTIFICATE HOLDER</div>
            <div style={{ position: 'relative', width: '100%', marginBottom: 6 }}>
              <i className="pi pi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 13, zIndex: 1, pointerEvents: 'none' }} />
              <AutoComplete
                value={holderSearchQuery}
                suggestions={holderSuggestions.map((h) => ({ label: h.name, value: h }))}
                completeMethod={(e) => setHolderSearchQuery(e.query)}
                onChange={(e) => { if (typeof e.value === 'string') setHolderSearchQuery(e.value); }}
                field="label"
                placeholder="Search for a certificate holder"
                style={{ width: '100%' }}
                inputStyle={{ width: '100%', paddingLeft: 30 }}
                disabled
                onSelect={(e) => {
                  const h = e.value.value;
                  setHolderName(h.name || '');
                  setHolderAddress(h.addressLine1 || '');
                  setHolderCity(h.city || '');
                  setHolderState(h.stateName || '');
                  setHolderZip(h.zipCode || '');
                  setHolderSearchQuery('');
                }}
              />
            </div>
            <InputText maxLength={100} value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" disabled />
            <InputText maxLength={100} value={holderAddress} onChange={(e) => setHolderAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" disabled />
            <InputText maxLength={100} value={holderCity} onChange={(e) => setHolderCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" disabled />
            <div className="masterCert__addressGrid">
              <InputText placeholder="Street" className="masterCert__input masterCert__inputFull" disabled />
              <InputText maxLength={50} value={holderState} onChange={(e) => setHolderState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" disabled />
              <InputText maxLength={20} value={holderZip} onChange={(e) => setHolderZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" disabled />
            </div>
          </div>
          <div className="masterCert__cancellationBox">
            <div className="masterCert__sectionHeader">CANCELLATION</div>
            <div className="masterCert__cancellationText">
              Should any of the above described policies be cancelled before the expiration date thereof, notice will be delivered in accordance with the policy provisions.
            </div>
            <div className="masterCert__authRepLabel">AUTHORIZED REPRESENTATIVE</div>
            <div className="masterCert__authRepRow">
              <Dropdown value={authRepSignature} onChange={(e) => setAuthRepSignature(e.value)} options={signatureOptions} optionLabel="label" optionValue="value" placeholder="Select signature:" className="masterCert__input masterCert__authRepDropdown" emptyMessage="No available options" />
              <Button label="Create New" className="p-button-outlined carrierDialog__secondaryBtn" onClick={() => setShowAddSignature(true)} />
            </div>
              {signaturePreviewUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={signaturePreviewUrl} alt="Signature preview" style={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
                </div>
              )}
          </div>
        </div>

        <div className="masterCert__acordFooter">
          <div>ACORD 21 (2009/12)</div>
          <div>&copy; 1988-2014 ACORD CORPORATION. All rights reserved.</div>
        </div>
      </div>

      <CreateNewCarriers open={showCreateCarrier} onClose={() => setShowCreateCarrier(false)} />
      <AddNewSignature open={showAddSignature} onClose={() => setShowAddSignature(false)} onSaved={refetchSignatures} />
    </>
  );
}
